/**
 * Movement Preset Functions
 * 
 * Reusable movement behaviors that can be called from any NPC behavior.
 * Just functions - no classes, no state machines.
 */

import type { AITickAPI } from '../hooks/NPCHookAPI';

// ============================================
// PATROL MOVEMENT
// ============================================

export interface PatrolState {
  direction: number;  // 1 = right, -1 = left
  minT: number;
  maxT: number;
}

/**
 * Patrol back and forth within T bounds on the spline.
 * Reverses direction when reaching patrol limits.
 */
export const patrolTick = (api: AITickAPI, state: PatrolState): void => {
  // Check bounds and reverse
  if (api.npcT >= state.maxT && state.direction > 0) {
    state.direction = -1;
  } else if (api.npcT <= state.minT && state.direction < 0) {
    state.direction = 1;
  }
  
  api.actions.setMovement(state.direction);
  api.actions.setFacing(state.direction);
};

// ============================================
// IDLE MOVEMENT
// ============================================

/**
 * Stand still - no movement.
 */
export const idleTick = (api: AITickAPI): void => {
  api.actions.setMovement(0);
};

// ============================================
// FOLLOW MOVEMENT
// ============================================

export interface FollowState {
  maxDistance: number;  // Start following when player is further than this
  minDistance: number;  // Stop following when closer than this
}

/**
 * Follow the player when they're too far away.
 * Uses spline T values for direction.
 */
export const followTick = (api: AITickAPI, state: FollowState): void => {
  if (api.playerDistance > state.maxDistance) {
    // Move toward player
    const direction = api.playerT > api.npcT ? 1 : -1;
    api.actions.setMovement(direction);
    api.actions.setFacing(direction);
  } else if (api.playerDistance < state.minDistance) {
    // Close enough, stop
    api.actions.setMovement(0);
  }
  // Between min and max: maintain current movement
};

// ============================================
// WANDER MOVEMENT
// ============================================

export interface WanderState {
  direction: number;
  timer: number;
  minWait: number;
  maxWait: number;
}

/**
 * Wander randomly - change direction periodically.
 */
export const wanderTick = (api: AITickAPI, state: WanderState): void => {
  state.timer -= api.deltaTime;
  
  if (state.timer <= 0) {
    // Pick new random direction (including stop)
    const rand = Math.random();
    if (rand < 0.3) {
      state.direction = 0;  // Stop for a bit
    } else if (rand < 0.65) {
      state.direction = 1;  // Go right
    } else {
      state.direction = -1; // Go left
    }
    
    // Random wait time before next change
    state.timer = state.minWait + Math.random() * (state.maxWait - state.minWait);
  }
  
  api.actions.setMovement(state.direction);
  if (state.direction !== 0) {
    api.actions.setFacing(state.direction);
  }
};

// ============================================
// FACTORY HELPERS
// ============================================

/** Create initial patrol state from config */
export const createPatrolState = (
  spawnT: number,
  leftDistance: number,
  rightDistance: number,
  splineLength: number
): PatrolState => {
  const leftT = leftDistance / splineLength;
  const rightT = rightDistance / splineLength;
  
  return {
    direction: 1,
    minT: Math.max(0, spawnT - leftT),
    maxT: Math.min(1, spawnT + rightT),
  };
};

/** Create initial follow state from config */
export const createFollowState = (
  maxDistance: number = 4.0,
  minDistance: number = 2.0
): FollowState => ({
  maxDistance,
  minDistance,
});

/** Create initial wander state */
export const createWanderState = (
  minWait: number = 1.0,
  maxWait: number = 3.0
): WanderState => ({
  direction: 0,
  timer: 0,
  minWait,
  maxWait,
});
